import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface DocumentRow {
  readonly documentExchangeId: string;
  readonly documentCode: string;
  readonly title: string;
  readonly documentType: string;
  readonly classification: string;
  readonly status: string;
}

const DOCUMENT_TYPES = ['CONTRACT', 'WORK_ORDER', 'CAMPAIGN_FILE', 'CREATIVE_ASSET', 'INVOICE', 'COMPLIANCE_DOCUMENT', 'REPORT', 'APPROVAL'];
const CLASSIFICATIONS = ['PUBLIC', 'INTERNAL', 'CONFIDENTIAL', 'RESTRICTED'];
const PAGE = 25;

/**
 * M42 §27 DocumentExchangeCenter (§11 / BR-03). Governed exchange of documents
 * with external stakeholders: share an authoritative document and download
 * shared documents. Viewing needs M42_DOCUMENT_VIEW, sharing needs
 * M42_DOCUMENT_SHARE, downloading needs M42_DOCUMENT_DOWNLOAD.
 */
export function DocumentExchangeCenter(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<DocumentRow[]>([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [portalUserId, setPortalUserId] = useState('');
  const [title, setTitle] = useState('');
  const [documentType, setDocumentType] = useState('CONTRACT');
  const [classification, setClassification] = useState('CONFIDENTIAL');
  const [storageRef, setStorageRef] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (typeFilter) params.set('documentType', typeFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: DocumentRow[]; total: number }>(`/v1/portal/documents?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, typeFilter, page, reload]);

  const share = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!title.trim()) { setActionError(t('m42.docs.titleRequired')); return; }
    try {
      await apiRequest('/v1/portal/documents/share', {
        method: 'POST',
        body: {
          portalUserId: portalUserId.trim() || null,
          title: title.trim(),
          documentType,
          classification,
          storageModule: 'M35',
          storageRef: storageRef.trim() || 'ref',
        },
        token,
      });
      setNotice(t('m42.docs.shared'));
      setTitle('');
      setStorageRef('');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.docs.notAuthShare'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const download = async (id: string): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/portal/documents/${id}/download`, { method: 'POST', body: {}, token });
      setNotice(t('m42.common.downloadRecorded'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.docs.notAuthDownload'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="document-exchange">
      <h1>{t('m42.docs.title')}</h1>

      <section aria-label="document-share">
        <h2>{t('m42.docs.shareHeading')}</h2>
        <form aria-label="share-form" onSubmit={(e) => void share(e)}>
          <RecordPicker<{ portalUserId: string; displayName?: string; email?: string }>
            endpoint="/v1/portal/users?limit=200"
            idField="portalUserId"
            label={t('m42.common.portalUser')}
            testId="field-portal-user"
            value={portalUserId}
            optionLabel={(u) => u.displayName ?? u.email ?? u.portalUserId}
            onPick={setPortalUserId}
            emptyText="No portal user on file"
          />
          <label>{t('m42.common.title')}<input aria-label="field-document-title" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label>{t('m42.common.type')}
            <select aria-label="field-document-type" value={documentType} onChange={(e) => setDocumentType(e.target.value)}>
              {DOCUMENT_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <label>{t('m42.docs.classificationLabel')}
            <select aria-label="field-classification" value={classification} onChange={(e) => setClassification(e.target.value)}>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m42.docs.storageRefLabel')}<input aria-label="field-storage-ref" value={storageRef} onChange={(e) => setStorageRef(e.target.value)} /></label>
          <Button variant="primary" type="submit" aria-label="submit-share">{t('m42.docs.shareBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="document-action-error">{actionError}</p>}
        {notice && <p aria-label="document-notice">{notice}</p>}
      </section>

      <section aria-label="document-filters">
        <label>{t('m42.common.type')}
          <select aria-label="filter-document-type" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}>
            <option value="">{t('m42.common.all')}</option>
            {DOCUMENT_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert" aria-label="document-error">{error}</p>}
      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="document-total">{t('m42.docs.total', { count: total })}</p>
          <DataTable
            ariaLabel="document-table"
            rows={rows}
            rowKey={(d) => d.documentExchangeId}
            emptyMessage={t('m42.docs.empty')}
            columns={[
              { key: 'code', header: t('m42.common.code'), cellAriaLabel: (d) => `document-code-${d.documentExchangeId}`, render: (d) => d.documentCode },
              { key: 'title', header: t('m42.common.title'), render: (d) => d.title },
              { key: 'type', header: t('m42.common.type'), render: (d) => d.documentType },
              { key: 'class', header: t('m42.common.classCol'), render: (d) => d.classification },
              { key: 'status', header: t('m42.common.status'), cellAriaLabel: (d) => `document-status-${d.documentExchangeId}`, render: (d) => d.status },
              { key: 'actions', header: t('m42.common.actions'), render: (d) => (
                <Button variant="secondary" type="button" aria-label={`download-${d.documentExchangeId}`} onClick={() => void download(d.documentExchangeId)}>{t('m42.common.download')}</Button>
              ) },
            ]}
          />

          <nav aria-label="document-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m42.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m42.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m42.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
